/**
 * Rechtsdokumente (Betreiber-Sicht): plattformweite Dokumente (AGB,
 * Datenschutzvereinbarung, Nutzungsvereinbarung) anlegen, neue Versionen
 * veröffentlichen (Upload = Publish) und Zustimmungs-Statistiken einsehen.
 * Neue Version mit Zustimmungspflicht -> Consent-Gate bei allen Nutzern.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify, Title } from 'react-admin';
import {
    Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
    Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
    GlobalDocument, GlobalDocumentVersion, createGlobalDocument,
    deleteGlobalDocument, downloadGlobalDocumentVersion, getConsentStats,
    listGlobalDocuments, uploadGlobalDocumentVersion,
} from '../services/documentService';

const GlobalDocuments = () => {
    const notify = useNotify();
    const [documents, setDocuments] = useState<GlobalDocument[]>([]);
    const [loading, setLoading] = useState(true);

    // Neues Dokument
    const [newSlug, setNewSlug] = useState('');
    const [newTitle, setNewTitle] = useState('');

    // Versions-Upload je Dokument
    const [requiresConsent, setRequiresConsent] = useState(true);
    const [uploadTarget, setUploadTarget] = useState<GlobalDocument | null>(null);
    const fileInput = useRef<HTMLInputElement>(null);

    // Statistik-Dialog
    const [stats, setStats] = useState<Awaited<ReturnType<typeof getConsentStats>> | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setDocuments(await listGlobalDocuments());
        } catch (e: any) {
            notify(e.message || 'Rechtsdokumente konnten nicht geladen werden', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { load(); }, [load]);

    const handleCreate = async () => {
        try {
            await createGlobalDocument(newSlug.trim(), newTitle.trim());
            notify('Dokument angelegt — jetzt die erste Version hochladen', { type: 'success' });
            setNewSlug('');
            setNewTitle('');
            await load();
        } catch (e: any) {
            notify(e.message || 'Anlegen fehlgeschlagen', { type: 'error' });
        }
    };

    const handleUploadVersion = async () => {
        const file = fileInput.current?.files?.[0];
        if (!uploadTarget || !file) {
            notify('Bitte eine Datei wählen', { type: 'warning' });
            return;
        }
        try {
            const version = await uploadGlobalDocumentVersion(uploadTarget.uuid, file, requiresConsent);
            notify(requiresConsent
                ? `Version ${version.version} veröffentlicht — alle Nutzer müssen erneut zustimmen`
                : `Version ${version.version} veröffentlicht (ohne Zustimmungspflicht)`,
                { type: 'success' });
            setUploadTarget(null);
            if (fileInput.current) fileInput.current.value = '';
            await load();
        } catch (e: any) {
            notify(e.message || 'Upload fehlgeschlagen', { type: 'error' });
        }
    };

    const handleDelete = async (doc: GlobalDocument) => {
        if (!window.confirm(`Rechtsdokument „${doc.title}" samt aller Versionen und Zustimmungen löschen?`)) return;
        try {
            await deleteGlobalDocument(doc.uuid);
            notify('Dokument gelöscht', { type: 'success' });
            await load();
        } catch (e: any) {
            notify(e.message || 'Löschen fehlgeschlagen', { type: 'error' });
        }
    };

    const handleStats = async (doc: GlobalDocument) => {
        try {
            setStats(await getConsentStats(doc.uuid));
        } catch (e: any) {
            notify(e.message || 'Statistik konnte nicht geladen werden', { type: 'error' });
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Title title="Rechtsdokumente" />
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom>Rechtsdokumente</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Plattformweite Dokumente (AGB, Datenschutzvereinbarung, Nutzungsvereinbarung).
                        Wird eine neue Version mit Zustimmungspflicht veröffentlicht, müssen alle Nutzer
                        beim nächsten App-Aufruf erneut zustimmen.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                        <TextField label="Schlüssel (z.B. agb)" size="small" value={newSlug}
                            onChange={(e) => setNewSlug(e.target.value)} sx={{ width: 200 }} />
                        <TextField label="Titel" size="small" value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)} sx={{ width: 300 }} />
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreate}
                            disabled={!newSlug.trim() || !newTitle.trim()}>
                            Dokument anlegen
                        </Button>
                    </Box>

                    {loading ? (
                        <CircularProgress size={28} />
                    ) : documents.length === 0 ? (
                        <Typography color="text.secondary">Noch keine Rechtsdokumente angelegt.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Titel</TableCell>
                                    <TableCell>Schlüssel</TableCell>
                                    <TableCell>Aktuelle Version</TableCell>
                                    <TableCell>Zustimmungspflicht</TableCell>
                                    <TableCell>Veröffentlicht</TableCell>
                                    <TableCell align="right">Aktionen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documents.map((doc) => (
                                    <TableRow key={doc.uuid}>
                                        <TableCell>{doc.title}</TableCell>
                                        <TableCell><code>{doc.slug}</code></TableCell>
                                        <TableCell>
                                            {doc.currentVersion ? `v${doc.currentVersion.version}` : <Chip size="small" label="keine Version" />}
                                        </TableCell>
                                        <TableCell>
                                            {doc.currentVersion
                                                ? (doc.currentVersion.requiresConsent
                                                    ? <Chip size="small" color="warning" label="ja" />
                                                    : <Chip size="small" variant="outlined" label="nein" />)
                                                : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {doc.currentVersion
                                                ? new Date(doc.currentVersion.publishedAt).toLocaleDateString('de-DE')
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" title="Neue Version veröffentlichen"
                                                onClick={() => setUploadTarget(doc)}>
                                                <UploadFileIcon fontSize="small" />
                                            </IconButton>
                                            {doc.currentVersion && (
                                                <IconButton size="small" title="Aktuelle Version herunterladen"
                                                    onClick={() => downloadGlobalDocumentVersion(doc.currentVersion!.uuid, doc.currentVersion!.fileName)
                                                        .catch((e) => notify(e.message, { type: 'error' }))}>
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton size="small" title="Zustimmungs-Statistik" onClick={() => handleStats(doc)}>
                                                <BarChartIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" title="Löschen" onClick={() => handleDelete(doc)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Versions-Upload */}
            <Dialog open={uploadTarget !== null} onClose={() => setUploadTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Neue Version: {uploadTarget?.title}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <input type="file" ref={fileInput} accept=".pdf,.doc,.docx" />
                        <FormControlLabel
                            control={<Checkbox checked={requiresConsent}
                                onChange={(e) => setRequiresConsent(e.target.checked)} />}
                            label="Zustimmung aller Nutzer erforderlich (Consent-Gate)"
                        />
                        <Typography variant="body2" color="text.secondary">
                            Mit Veröffentlichung ist diese Version sofort die gültige Fassung.
                            Mit Zustimmungspflicht wird jeder Nutzer beim nächsten App-Aufruf zur
                            Zustimmung aufgefordert und die Zustimmung revisionssicher protokolliert.
                        </Typography>
                        <Box>
                            <Button variant="contained" startIcon={<UploadFileIcon />} onClick={handleUploadVersion}>
                                Version veröffentlichen
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Zustimmungs-Statistik */}
            <Dialog open={stats !== null} onClose={() => setStats(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Zustimmungen: {stats?.document.title}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Aktive Nutzer gesamt: {stats?.eligibleUsers}
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Version</TableCell>
                                <TableCell>Veröffentlicht</TableCell>
                                <TableCell>Zustimmungspflicht</TableCell>
                                <TableCell align="right">Zustimmungen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stats?.versions.map((v: GlobalDocumentVersion) => (
                                <TableRow key={v.uuid}>
                                    <TableCell>v{v.version}</TableCell>
                                    <TableCell>{new Date(v.publishedAt).toLocaleDateString('de-DE')}</TableCell>
                                    <TableCell>{v.requiresConsent ? 'ja' : 'nein'}</TableCell>
                                    <TableCell align="right">
                                        {v.consentCount}{stats && ` / ${stats.eligibleUsers}`}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default GlobalDocuments;
